/**
 * useMentionFileCandidates
 *
 * Builds the candidate list for the composer's `!` file-mention picker,
 * merging two sources into one list:
 *   - the active conversation's own VFS files (source: "Conversation"), and
 *   - files from the knowledge bases the agent can reach (source: the KB name).
 *
 * Each candidate's `id` encodes where the file lives via `buildFileRefId`
 * (bare path for conversation files, `${kbId}::${path}` for KB files), so the
 * mention survives the Lexical round-trip and `extractMentions` can recover
 * the owning KB on send. `metadata.sourceLabel` drives the per-row source tag.
 *
 * Cost note: walking a KB tree is one HTTP call per folder (`walkKbTree`), so
 * the KB walks are gated behind `enabled` — the composer flips it on only when
 * the `!` popover first opens, and the queries share the exact react-query key
 * the files-backend KB adapter uses (`KB_ENTRIES_KEY`), so a KB already browsed
 * in AuiFiles is served from cache.
 */

import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import type { Unstable_Mention } from '@assistant-ui/react';
import { useVfsFiles } from './useVfsFiles';
import { useMeAgent } from './useAgents';
import { useAllKnowledgeBases } from './useKnowledgeBases';
import {
  KB_ENTRIES_KEY,
  walkKbTree,
} from '../components/aui/components/files-backend/kb';
import { MENTION_TYPE_FILE, buildFileRefId } from '../utils/directive-mentions';

/** Source-tag label for files that live in the conversation's own VFS. */
const CONVERSATION_SOURCE = 'Conversation';

/** Upper bound on rendered candidates — the adapter still fuzzy-filters the
 *  full set; this just caps a pathologically large merge. */
const MAX_CANDIDATES = 200;

/** Hide system / dotfile paths (e.g. `.screenshots/…`) — same rule the
 *  composer and AuiFiles use for the tree view. */
function isHiddenPath(path: string): boolean {
  return path.split('/').some((seg) => seg.startsWith('.'));
}

export interface UseMentionFileCandidatesArgs {
  conversationId: string | undefined;
  agentId: string | undefined;
  /** KB walks only fire once this is true — wired to the `!` popover opening
   *  so they don't run on every composer mount. */
  enabled: boolean;
}

export function useMentionFileCandidates({
  conversationId,
  agentId,
  enabled,
}: UseMentionFileCandidatesArgs): Unstable_Mention[] {
  const { entries: conversationFiles } = useVfsFiles(conversationId);
  const { data: agent } = useMeAgent(agentId);
  const { items: allKbs } = useAllKnowledgeBases();

  // KB scope: the agent's granted KBs, intersected with the KBs the user can
  // actually see (gives us a display name + drops grants they've lost access
  // to). When no agent / no grants are available (e.g. the dev preview or
  // while loading), fall back to every visible KB — a broad net, but the
  // authorize step on the backend is the real gate (see
  // context/kb-file-mention-contract.md).
  const kbScope = useMemo(() => {
    const byId = new Map(allKbs.map((kb) => [kb.kbId, kb] as const));
    const grants = agent?.knowledgeBases;
    if (grants && grants.length > 0) {
      return grants
        .map((g) => byId.get(g.kbId))
        .filter((kb): kb is NonNullable<typeof kb> => !!kb);
    }
    return allKbs;
  }, [agent?.knowledgeBases, allKbs]);

  const kbQueries = useQueries({
    queries: kbScope.map((kb) => ({
      queryKey: KB_ENTRIES_KEY(kb.kbId),
      queryFn: () => walkKbTree(kb.kbId),
      staleTime: 5_000,
      enabled,
    })),
  });

  // Stable recompute signal: changes only when a KB's flat list actually
  // refreshes (react-query bumps `dataUpdatedAt`), not on every render. Lets
  // the memo below depend on KB data without listing the per-render
  // `kbQueries` array identity.
  const kbSignature = kbScope
    .map((kb, i) => `${kb.kbId}:${kbQueries[i]?.dataUpdatedAt ?? 0}`)
    .join('|');

  return useMemo<Unstable_Mention[]>(() => {
    const out: Unstable_Mention[] = [];

    // Conversation VFS files.
    for (const f of conversationFiles) {
      if (isHiddenPath(f.path)) continue;
      out.push({
        id: buildFileRefId(undefined, f.path),
        type: MENTION_TYPE_FILE,
        label: f.name,
        metadata: { description: f.path, sourceLabel: CONVERSATION_SOURCE },
      });
    }

    // KB files.
    kbScope.forEach((kb, i) => {
      const nodes = kbQueries[i]?.data?.items ?? [];
      for (const n of nodes) {
        if (n.kind !== 'file') continue;
        if (isHiddenPath(n.path)) continue;
        out.push({
          id: buildFileRefId(kb.kbId, n.path),
          type: MENTION_TYPE_FILE,
          label: n.name,
          metadata: { description: n.path, sourceLabel: kb.name, kbId: kb.kbId },
        });
      }
    });

    // Conversation files first, then grouped by source name, then by path.
    out.sort((a, b) => {
      const aKb = a.metadata?.kbId as string | undefined;
      const bKb = b.metadata?.kbId as string | undefined;
      if (!aKb && bKb) return -1;
      if (aKb && !bKb) return 1;
      const aSrc = (a.metadata?.sourceLabel as string | undefined) ?? '';
      const bSrc = (b.metadata?.sourceLabel as string | undefined) ?? '';
      if (aSrc !== bSrc) return aSrc.localeCompare(bSrc);
      const ap = (a.metadata?.description as string | undefined) ?? a.label;
      const bp = (b.metadata?.description as string | undefined) ?? b.label;
      return ap.localeCompare(bp);
    });

    return out.slice(0, MAX_CANDIDATES);
    // `kbQueries` is intentionally read via `kbSignature` rather than listed
    // directly (its array identity changes every render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationFiles, kbScope, kbSignature]);
}
